// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { ConfigurationType } from "../types/ConfigurationType";

// GraphQL
import { GetConfigQuery } from "@/graphql";

interface ConfigurationProvider {
  // Getters
  getConfiguration: () => ConfigurationType;

  // Methods
  showInstallationField: (field: string, calculationTypeId: number | null) => boolean;
  useStageDefaultEmissionByStage: (calculationTypeId: number | null) => boolean;
  mapData: (data: GetConfigQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useConfiguration = defineStore("ConfigurationProvider", (): ConfigurationProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const configuration = ref<ConfigurationType>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  function getConfiguration() {
    return configuration.value;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setConfiguration(newConfigucation: ConfigurationType) {
    configuration.value = newConfigucation;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function showInstallationField(field: string, calculationTypeId: number | null) {
    if (!configuration.value) return false;

    // Attempt to find a config by calculationTypeId - or fall back to the default config (no calculationTypeId)
    const config =
      configuration.value.find((x) => x.calculationTypeId === calculationTypeId) ||
      configuration.value.find((x) => typeof x.calculationTypeId !== "number");

    return config?.installationFields.includes(field) ? true : false;
  }

  // Whether the country/calculationType allows standard values (default emissions)
  // to be toggled on a per-stage basis. Falls back to the default config (no
  // calculationTypeId) when there is no calculationType-specific entry.
  function useStageDefaultEmissionByStage(calculationTypeId: number | null) {
    if (!configuration.value) return false;

    const config =
      configuration.value.find((x) => x.calculationTypeId === calculationTypeId) ||
      configuration.value.find((x) => typeof x.calculationTypeId !== "number");

    return config?.useStageDefaultEmissionByStage ?? false;
  }

  function mapData(data: GetConfigQuery) {
    const { countryConfig } = data;

    // Map all models
    if (!countryConfig) return;
    setConfiguration(countryConfig);
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    // Getters
    getConfiguration,

    // Methods
    showInstallationField,
    useStageDefaultEmissionByStage,
    mapData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useConfiguration;
